import {
    useState,
    forwardRef,
    useImperativeHandle,
    ReactNode,
    MouseEvent
} from "react";
import {
    Button,
    Popover,
    Box,
    ButtonProps,
    PopoverOrigin // 1. Importiamo il tipo per le origin
} from "@mui/material";

export interface ButtonWithPopoverRef {
    open: () => void;
    close: () => void;
    toggle: () => void;
}

export interface ButtonWithPopoverProps extends ButtonProps {
    popoverContent: ReactNode;
    open?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
    anchorOrigin?: PopoverOrigin;
    transformOrigin?: PopoverOrigin;
}

export const ButtonWithPopover = forwardRef<
    ButtonWithPopoverRef,
    ButtonWithPopoverProps
>(function ButtonWithPopover(
    {
        popoverContent,
        open: controlledOpen,
        onOpen,
        onClose,
        onClick,
        anchorOrigin = { vertical: "top", horizontal: "left" },
        transformOrigin = { vertical: "bottom", horizontal: "center" },
        ...buttonProps
    },
    ref
) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : Boolean(anchorEl);

    const handleOpen = (event?: MouseEvent<HTMLButtonElement>) => {
        if (!isControlled && event) {
            setAnchorEl(event.currentTarget);
        }
        onOpen?.();
    };

    const handleClose = () => {
        if (!isControlled) {
            setAnchorEl(null);
        }
        onClose?.();
    };

    const handleToggle = (event: MouseEvent<HTMLButtonElement>) => {
        if (open) {
            handleClose();
        } else {
            handleOpen(event);
        }

        onClick?.(event);
    };

    useImperativeHandle(ref, () => ({
        open: () => handleOpen(),
        close: () => handleClose(),
        toggle: () => (open ? handleClose() : handleOpen())
    }));

    return (
        <>
            <Button {...buttonProps} onClick={handleToggle} />

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
            >
                <Box sx={{ p: 2 }}>
                    {popoverContent}
                </Box>
            </Popover>
        </>
    );
});